import {
  type IGetSocialApplyResponse,
  displayOrDownloadUrlDocument,
  formatName,
  usePatchSendFupToOperator,
  usePreviewFup,
} from "@api";
import type { IDocument } from "@bbj-types/document";
import type { IFupLogo } from "@bbj-types/organisation";
import type { PartnerDecision } from "@bbj-types/socialApply/enum";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGlobalContext } from "@root/context";
import { useForm } from "react-hook-form";
import { formatPartnerFeedback, formatSponsorsLogo } from "./format";
import { type TPartnerFeedbackFormResolver, partnerFeedbackFormSchema } from "./partnerFeedback/resolver";

export const useFupPartnerForm = (
  socialApply: Required<IGetSocialApplyResponse>,
  onSendFup: (result: { fupDocument: IDocument; decision: PartnerDecision }) => void,
  fupLogos: IFupLogo[]
) => {
  const { user } = useGlobalContext();
  const [{ loading: loadingPreview }, previewFup] = usePreviewFup();
  const [{ loading: loadingSend }, sendFup] = usePatchSendFupToOperator();

  const sponsorsLogo = socialApply.socialOffer.financingModes?.includes("fse")
    ? fupLogos.map((logo) => (logo.value === "fse" ? { ...logo, required: true } : logo))
    : fupLogos;

  const { control, handleSubmit, resetField, getValues, setValue } = useForm<TPartnerFeedbackFormResolver>({
    resolver: yupResolver(partnerFeedbackFormSchema),
    defaultValues: {
      partnerFeedback: {
        accompanierName: formatName(user),
        name: socialApply.socialOffer.provider?.name,
        email: user.email,
        phone: user.phone,
      },
      sponsorsLogo: [],
    },
  });

  const onDisplayPreviewPdf = handleSubmit(async (data) => {
    const response = await previewFup({
      name: socialApply.fup.name,
      beneficiary: socialApply.fup.beneficiary,
      prescription: {
        approveDemand: socialApply.fup.prescription.approveDemand,
        email: socialApply.fup.prescription.email,
        phone: socialApply.fup.prescription.phone,
        goal: socialApply.fup.prescription.goal,
        rsaReferentName: socialApply.fup.prescription.rsaReferentName,
        structure: socialApply.fup.prescription.structure,
        validatePrescription: socialApply.fup.prescription.validatePrescription,
        prescriptionDate: socialApply.fup.prescription.prescriptionDate,
      },
      partnerFeedback: formatPartnerFeedback(data.partnerFeedback),
      sponsorsLogo: formatSponsorsLogo(
        fupLogos,
        getValues("sponsorsLogo"),
        !!socialApply.socialOffer.financingModes?.includes("fse")
      ).map((logo) => ({
        label: logo.label,
        uri: logo.uri,
      })),
    });
    if (response.status === 200) {
      void displayOrDownloadUrlDocument(response.data, "display", "Aperçu FUP", "application/pdf");
    }
  });

  const onSendFupToOperator = handleSubmit(async (data) => {
    const res = await sendFup(socialApply._id, {
      partnerFeedback: formatPartnerFeedback(data.partnerFeedback),
      sponsorsLogo: formatSponsorsLogo(
        fupLogos,
        getValues("sponsorsLogo"),
        !!socialApply.socialOffer.financingModes?.includes("fse")
      ).map((logo) => ({
        label: logo.label,
        uri: logo.uri,
      })),
    });
    onSendFup({ fupDocument: res.data.document, decision: data.partnerFeedback.decision.partnerDecision });
  });

  return {
    control,
    getValues,
    setValue,
    resetField,
    onDisplayPreviewPdf,
    loadingPreview,
    onSendFupToOperator,
    loadingSend,
    sponsorsLogo,
  };
};
